import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import type { AddSlotContext } from '@mlm/ui';
import { getMe, getUnplacedDownline, placeSlot, redeemKit, type UnplacedSlot } from '../lib/api';
import { prettyRedeemError } from '../lib/redeem-errors';

export type MemberAddSlotDialogProps = {
  ctx: AddSlotContext | null;
  view: 'unilevel' | 'binary';
  anchorSlotId: string;
  /**
   * When false, the "Redeem new code" (activate a new slot) tab is hidden and
   * the dialog only places existing unplaced downline slots. Gated by the tenant
   * `genealogy_activate_slot` flag. Default true.
   */
  allowRedeem?: boolean;
  onClose: () => void;
  /**
   * Fired after a slot is successfully placed/activated under `ctx.parentSlotId`.
   * The new node sits one level below the seat we filled — which may be deeper
   * than the tree's current fetch depth — so the page uses this to extend the
   * loaded depth and the node shows without a manual reload or caret toggle.
   */
  onPlaced?: () => void;
};

type Mode = 'place' | 'create';

export function MemberAddSlotDialog({
  ctx,
  view,
  anchorSlotId,
  allowRedeem = true,
  onClose,
  onPlaced,
}: MemberAddSlotDialogProps) {
  const qc = useQueryClient();
  const open = ctx !== null;
  const showBinarySide = view === 'binary';

  // Pool of slots in this member's downline that still need a binary seat.
  // Only relevant for binary placements (unilevel has no seat picker).
  const poolQ = useQuery({
    queryKey: ['unplaced-downline', anchorSlotId],
    queryFn: () => getUnplacedDownline(anchorSlotId),
    enabled: open && showBinarySide,
  });
  // Sponsor editability for this member's next activation (per tenant toggles).
  // This dialog is always an extra-slot context; when the tenant disabled editing
  // for extra slots / self-stack, the server ignores a typed sponsor and uses the
  // member's default — so we lock the field to that default here.
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, enabled: open });
  const sponsorEditable = meQ.data?.sponsorEditable !== false;
  const defaultSponsorSlotCode = meQ.data?.defaultSponsorSlotCode ?? '';
  const pool = useMemo<UnplacedSlot[]>(() => poolQ.data ?? [], [poolQ.data]);
  const poolEmpty = !poolQ.isLoading && pool.length === 0;

  // Default to "place existing" when there's a pool to draw from; otherwise
  // go straight to redeem-a-new-code. Members can toggle either way.
  const [mode, setMode] = useState<Mode>('place');
  const [pickedSlotId, setPickedSlotId] = useState<string>('');
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [sponsorCode, setSponsorCode] = useState('');
  const [side, setSide] = useState<'L' | 'R'>('L');
  const [error, setError] = useState<string | null>(null);

  // Reset all transient state every time the dialog re-opens on a fresh seat.
  useEffect(() => {
    if (!open || !ctx) return;
    setCode('');
    setPin('');
    setSponsorCode(ctx.parentSlotCode);
    setSide(ctx.position === 'R' ? 'R' : 'L');
    setPickedSlotId('');
    setError(null);
  }, [open, ctx]);

  // Once the pool resolves, pick a sensible mode + default selection. Members
  // viewing unilevel skip the picker entirely (no binary seat to fill). When
  // redeem is disabled (tenant flag off), stay in 'place' regardless — there's
  // no "activate new code" fallback.
  useEffect(() => {
    if (!open || !showBinarySide) {
      setMode(allowRedeem ? 'create' : 'place');
      return;
    }
    if (poolQ.isLoading) return;
    setMode(pool.length > 0 || !allowRedeem ? 'place' : 'create');
    if (pool[0]) setPickedSlotId(pool[0].slotId);
  }, [open, showBinarySide, poolQ.isLoading, pool, allowRedeem]);

  const placeMut = useMutation({
    meta: { successMessage: 'Slot placed.' },
    mutationFn: () => {
      if (!ctx || !pickedSlotId) throw new Error('missing_inputs');
      return placeSlot(pickedSlotId, {
        parentSlotId: ctx.parentSlotId,
        position: side,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['genealogy'] });
      qc.invalidateQueries({ queryKey: ['my-slots'] });
      qc.invalidateQueries({ queryKey: ['unplaced-downline'] });
      qc.invalidateQueries({ queryKey: ['onboarding'] });
      onPlaced?.();
      onClose();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string } } }).response?.data;
      setError(prettyPlaceError(data?.error ?? 'place_failed'));
    },
  });

  const redeemMut = useMutation({
    meta: { successMessage: 'Slot activated.' },
    mutationFn: () =>
      redeemKit({
        code,
        pin,
        // When editing is locked, don't send a sponsor — the server uses the
        // member's default (it would ignore a typed one anyway).
        sponsorCode: sponsorEditable ? sponsorCode || undefined : undefined,
        anchorSlotId,
        side: showBinarySide ? side : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['genealogy'] });
      qc.invalidateQueries({ queryKey: ['my-slots'] });
      qc.invalidateQueries({ queryKey: ['vault'] });
      qc.invalidateQueries({ queryKey: ['unplaced-downline'] });
      // Sponsor editability depends on slot count (first vs extra) — refresh it.
      qc.invalidateQueries({ queryKey: ['me'] });
      onPlaced?.();
      onClose();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string } } }).response?.data;
      setError(prettyRedeemError(data?.error ?? 'redeem_failed'));
    },
  });

  const busy = placeMut.isPending || redeemMut.isPending;
  const canPlace = mode === 'place' && !!pickedSlotId;
  const canRedeem = mode === 'create' && code.length > 0 && pin.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'place' ? 'Place an affiliate slot' : 'Activate a new slot'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'place'
              ? <>Place one of your unplaced affiliate slots at the {showBinarySide ? `${side === 'L' ? 'LEFT' : 'RIGHT'} seat under ` : 'next position under '}<span className="font-mono">{ctx?.parentSlotCode ?? '—'}</span>.</>
              : <>Enter the kit Code &amp; PIN from your Vault. The new slot will be placed under <span className="font-mono">{ctx?.parentSlotCode ?? '—'}</span>.</>
            }
          </DialogDescription>
        </DialogHeader>

        {/* Mode tabs — only show when both options are real (redeem allowed). */}
        {showBinarySide && !poolEmpty && allowRedeem && (
          <div className="flex rounded-md border p-0.5 text-sm">
            <button
              type="button"
              onClick={() => { setMode('place'); setError(null); }}
              disabled={busy}
              className={`flex-1 rounded-sm px-3 py-1.5 ${
                mode === 'place' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
              }`}
            >
              Place existing
              <span className="ml-1.5 text-xs opacity-70">({pool.length})</span>
            </button>
            <button
              type="button"
              onClick={() => { setMode('create'); setError(null); }}
              disabled={busy}
              className={`flex-1 rounded-sm px-3 py-1.5 ${
                mode === 'create' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
              }`}
            >
              Redeem new code
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          {mode === 'place' ? (
            <>
              {poolQ.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading unplaced slots…</p>
              ) : poolEmpty ? (
                <p className="text-sm text-muted-foreground">
                  {allowRedeem
                    ? 'No unplaced affiliate slots yet — redeem a code to activate a new one.'
                    : 'No unplaced slots to place. Activating new codes from here is turned off.'}
                </p>
              ) : (
                <div>
                  <label className="text-xs text-muted-foreground">Unplaced slot</label>
                  <select
                    value={pickedSlotId}
                    onChange={(e) => setPickedSlotId(e.target.value)}
                    className="flex h-9 w-full rounded-md border bg-background px-3 text-sm"
                  >
                    {pool.map((s) => (
                      <option key={s.slotId} value={s.slotId}>
                        {s.slotCode} · {s.userFullName ?? s.userEmail ?? '—'}
                        {s.membershipName ? ` · ${s.membershipName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Kit code</label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="ABCDEF123456"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">PIN</label>
                  <Input value={pin} onChange={(e) => setPin(e.target.value)} required />
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Sponsor slot code (override)</label>
                <Input
                  value={sponsorEditable ? sponsorCode : defaultSponsorSlotCode}
                  onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
                  placeholder="Leave blank for default"
                  readOnly={!sponsorEditable}
                  className={!sponsorEditable ? 'bg-muted' : undefined}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {sponsorEditable
                    ? 'Pre-filled from the slot you clicked. Clear it to use your default sponsor.'
                    : 'Locked by your administrator for this activation.'}
                </p>
              </div>
            </>
          )}

          {showBinarySide && (
            <div>
              <label className="text-xs text-muted-foreground">Binary side</label>
              <div className="mt-1 flex gap-2">
                {(['L', 'R'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setSide(p)}
                    className={`flex-1 rounded-md border py-1.5 text-sm ${
                      side === p
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    }`}
                  >
                    {p === 'L' ? 'Left' : 'Right'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {mode === 'place' ? (
            <Button onClick={() => placeMut.mutate()} disabled={!canPlace || busy}>
              {placeMut.isPending ? 'Placing…' : 'Place slot'}
            </Button>
          ) : (
            <Button onClick={() => redeemMut.mutate()} disabled={!canRedeem || busy}>
              {redeemMut.isPending ? 'Activating…' : 'Activate slot'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function prettyPlaceError(code: string): string {
  switch (code) {
    case 'already_placed':
      return 'This slot is already placed somewhere in the binary tree.';
    case 'position_occupied':
      return 'That seat was just taken by another action. Refresh and try again.';
    case 'forbidden':
      return "You don't have permission to place that slot.";
    case 'slot_not_found':
      return 'The slot could not be found.';
    default:
      return code.replace(/_/g, ' ');
  }
}
